import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useBulkDeleteSubmissions } from "hooks/useSubmissionMutations";
import pluralize from "pluralize";
import { Row } from "./WasteSubmissionsDataGrid";
import { partitionSubmissions } from "./utils";
import { useMemo } from "react";

type DeleteSubmissionsDialogProps = {
  open: boolean;
  onClose: () => void;
  submissions: Row[];
  onSuccess: () => void;
};

export function DeleteSubmissionsDialog({
  open,
  onClose,
  submissions,
  onSuccess,
}: DeleteSubmissionsDialogProps) {
  const alerts = useAlerts();
  const [bulkDeleteSubmissions, { loading }] = useBulkDeleteSubmissions();

  const { virtualEventIds, uniqueSubmissionIds } = useMemo(
    () => partitionSubmissions(submissions),
    [submissions]
  );

  const handleConfirm = async () => {
    try {
      await bulkDeleteSubmissions({
        variables: {
          input: {
            submissionIds: uniqueSubmissionIds,
            virtualEventIds: virtualEventIds,
          },
        },
        refetchQueries: ["WasteSubmissionsForStaff", "WasteSubmissionsStaffSummary"],
        awaitRefetchQueries: true,
      });

      alerts.success("Submission(s) deleted successfully");
      onSuccess();
      onClose();
    } catch (error) {
      alerts.error("Failed to delete submission(s)", error);
    }
  };

  return (
    <ConfirmDialog
      open={open}
      loading={loading}
      onClose={onClose}
      header="Delete Submissions"
      info={`Are you sure you want to delete the selected ${
        submissions.length > 1 ? submissions.length : ""
      } ${pluralize(
        "submission",
        submissions.length
      )}? This will only affect these instances of submissions and will not affect any recurring requirements. Any submission documents will also be deleted.`}
      onConfirm={handleConfirm}
    />
  );
}
